import React, { useRef, useEffect, useState, useContext } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, Inline, Text, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import Spinner from '@atlaskit/spinner';
import VideoIcon from '@atlaskit/icon/core/video';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import { MediaProvider } from '@atlaskit/media-client-react';
import type { MediaClientConfig } from '@atlaskit/media-core';

import { usePopupDwellTime } from '@atlassian/popup-dwell-time';

import { ExternalShareContext } from '@confluence/external-share-context/entry-points/ExternalShareContext';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { fg } from '@confluence/feature-gating';
import { createRendererMediaProvider } from '@confluence/fabric-media-support/entry-points/MediaProvider';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey';

import {
	useLoadLoomVideoDurations,
	useLoadMediaFileDurations,
} from './hooks/useLoadVideoDurations';
import type { ContentFeatures } from './featuresExtractor';
import { calculateImageReadTime } from './readTimeUtils';
export const i18n = defineMessages({
	readTime: {
		id: 'read-time.read-time-popup',
		defaultMessage: 'Read time break-down',
		description: 'Read time label',
	},
	videos: {
		id: 'read-time.read-time-popup-videos',
		defaultMessage: 'Videos',
		description: 'Videos label',
	},
	pageContent: {
		id: 'read-time.read-time-popup-page-content',
		defaultMessage: 'Page content',
		description: 'Page content label',
	},
	expands: {
		id: 'read-time.read-time-popup-expands',
		defaultMessage: 'Expands',
		description: 'Expands label',
	},
	expandTooltipDescription: {
		id: 'read-time.read-time-popup-expand-tooltip-description',
		defaultMessage: 'Loom videos and expands are excluded from read time estimates',
		description: 'Expand tooltip message',
	},
	readTimeDisclaimer: {
		id: 'read-time.read-time-popup-read-time-disclaimer',
		defaultMessage: 'Not included in read time',
		description: 'Read time disclaimer',
	},
});

const popupStyles = xcss({
	minWidth: '340px',
	position: 'relative',
	backgroundColor: 'elevation.surface.raised',
	border: `${token('border.width')} solid color.border`,
	borderRadius: 'radius.large',
	padding: 'space.250',
});

const headingContainerStyles = xcss({
	marginTop: 'space.negative.050',
	marginBottom: 'space.200',
});

const featureContainerStyles = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
});

const lastFeatureContainerStyles = xcss({
	marginBottom: 'space.0',
});

const baseIconStyles = xcss({
	paddingBlockStart: 'space.025',
	marginRight: 'space.100',
});

const expandIconStyles = xcss({
	borderRadius: 'radius.small',
	border: `${token('border.width')} solid ${token('color.border')}`,
	width: '20px',
	height: '20px',
	marginLeft: 'space.negative.025',
	marginRight: 'space.075',
});

const HorizontalDivider = xcss({
	borderBottom: `${token('border.width')} solid ${token('color.border')}`,
	height: '1px',
	width: '135px',
});

const formatVideoDuration = (ms: number): string => {
	const totalSeconds = Math.round(ms / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	if (minutes === 0) {
		return `${seconds} sec`;
	}
	if (seconds === 0) {
		return `${minutes} min`;
	}
	return `${minutes} min ${seconds} sec`;
};

const formatReadTimeInSeconds = (seconds: number): string => {
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = Math.floor(seconds % 60);

	if (minutes === 0) {
		return `${remainingSeconds} sec`;
	}
	if (remainingSeconds === 0) {
		return `${minutes} min`;
	}
	return `${minutes} min ${remainingSeconds} sec`;
};

export type ReadTimePopupProps = {
	features: ContentFeatures | null;
	featuresSkipExpand: ContentFeatures | null;
	source: string;
	contentId?: string;
};

export const ReadTimePopup = ({
	features,
	featuresSkipExpand,
	source,
	contentId = '',
}: ReadTimePopupProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const readTimePopupRef = useRef<HTMLDivElement>(null);
	const { getDwellTime } = usePopupDwellTime(readTimePopupRef);
	const { formatMessage } = useIntl();

	useEffect(() => {
		return () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'dwelled',
					actionSubject: 'readTime',
					actionSubjectId: 'readTimePopup',
					source,
					attributes: {
						dwellTime: getDwellTime(),
					},
				},
			}).fire();
		};
		// we only want to run this effect on un-mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!features && !featuresSkipExpand) {
		return null;
	}

	let expandsReadTime = 0;
	if (features && featuresSkipExpand) {
		if (features.wordCount !== featuresSkipExpand.wordCount) {
			expandsReadTime = ((features.wordCount - featuresSkipExpand.wordCount) / 265) * 60;
		}
	}

	const readTimeWithoutExpands = ((featuresSkipExpand?.wordCount || 0) / 265) * 60;
	const pageContentReadTime =
		readTimeWithoutExpands + calculateImageReadTime(featuresSkipExpand?.imageCount || 0);

	return (
		<Box ref={readTimePopupRef} testId="read-time-popup">
			<Stack xcss={popupStyles}>
				<Box key="readTime" xcss={[featureContainerStyles, headingContainerStyles]}>
					<Inline alignBlock="center" spread="space-between">
						<Heading size="medium">
							<FormattedMessage {...i18n.readTime} />
						</Heading>
					</Inline>
				</Box>
				<Box key="wordCountSkipExpand" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Inline alignBlock="center">
							<Box xcss={baseIconStyles}>
								<AlignTextLeftIcon
									color={token('color.text.subtle')}
									label={formatMessage(i18n.readTime)}
								/>
							</Box>
							<FormattedMessage {...i18n.pageContent} />
						</Inline>
						<Text>{formatReadTimeInSeconds(pageContentReadTime)}</Text>
					</Inline>
				</Box>

				<Box key="readTimeDisclaimer" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Text color="color.text.subtlest" size="small">
							<FormattedMessage {...i18n.readTimeDisclaimer} />
						</Text>
						<Box xcss={HorizontalDivider} />
					</Inline>
				</Box>
				<Box key="videoDuration" xcss={featureContainerStyles}>
					<Inline alignBlock="center" spread="space-between">
						<Inline alignBlock="center">
							<Box xcss={baseIconStyles}>
								<VideoIcon color={token('color.text.subtle')} label={formatMessage(i18n.videos)} />
							</Box>
							<FormattedMessage {...i18n.videos} />
						</Inline>
						<VideoDurations features={features} contentId={contentId} />
					</Inline>
				</Box>

				<Box key="expandsReadTime" xcss={[featureContainerStyles, lastFeatureContainerStyles]}>
					<Inline alignBlock="center" spread="space-between">
						<Inline alignBlock="center">
							<Flex alignItems="center" justifyContent="center" xcss={expandIconStyles}>
								<ChevronRightIcon
									color={token('color.text.subtle')}
									label={formatMessage(i18n.expands)}
									spacing="spacious"
									size="small"
								/>
							</Flex>
							<FormattedMessage {...i18n.expands} />
						</Inline>
						<Text>{formatReadTimeInSeconds(expandsReadTime)} </Text>
					</Inline>
				</Box>
			</Stack>
		</Box>
	);
};

const VideoDurations = ({
	features,
	contentId,
}: {
	features: ContentFeatures | null;
	contentId: string;
}) => {
	const { loomVideoDurations, isLoadingLoomDurations } = useLoadLoomVideoDurations(
		features?.looms || [],
	);

	if (!fg('platform_media-cc-page-duration-integration')) {
		if (isLoadingLoomDurations) {
			return <CommonReadTimePopupSpinner />;
		}
		return <Text>{formatVideoDuration(loomVideoDurations)}</Text>;
	}

	return (
		<VideoDurationsWithMedia
			features={features}
			contentId={contentId}
			loomVideoDurations={loomVideoDurations}
			isLoadingLoomDurations={isLoadingLoomDurations}
		/>
	);
};

const VideoDurationsWithMedia = ({
	features,
	contentId,
	loomVideoDurations,
	isLoadingLoomDurations,
}: {
	features: ContentFeatures | null;
	contentId: string;
	loomVideoDurations: number;
	isLoadingLoomDurations: boolean;
}) => {
	const [mediaClientConfig, setMediaClientConfig] = useState<MediaClientConfig | null>(null);
	const { isExternalShareRequest: isExternalShare } = useContext(ExternalShareContext);
	const spaceKey = useSpaceKey();

	useEffect(() => {
		if (contentId) {
			createRendererMediaProvider({
				contentId,
				mediaDspContentId: contentId,
				spaceKey,
				isExternalShare,
			}).then(({ viewMediaClientConfig }) => setMediaClientConfig(viewMediaClientConfig));
		}
	}, [contentId, spaceKey, isExternalShare]);

	if (!mediaClientConfig) {
		return <CommonReadTimePopupSpinner />;
	}

	return (
		<MediaProvider mediaClientConfig={mediaClientConfig}>
			<VideoDurationsContent
				features={features}
				contentId={contentId}
				loomVideoDurations={loomVideoDurations}
				isLoadingLoomDurations={isLoadingLoomDurations}
			/>
		</MediaProvider>
	);
};

const VideoDurationsContent = ({
	features,
	contentId,
	loomVideoDurations,
	isLoadingLoomDurations,
}: {
	features: ContentFeatures | null;
	contentId: string;
	loomVideoDurations: number;
	isLoadingLoomDurations: boolean;
}) => {
	const { mediaVideoDurations, isLoadingMediaDurations } = useLoadMediaFileDurations(
		features?.mediaFiles || [],
		`contentId-${contentId}`,
	);

	if (isLoadingMediaDurations || isLoadingLoomDurations) {
		return <CommonReadTimePopupSpinner />;
	}

	if (fg('platform_media-cc-page-duration-integration')) {
		return <Text>{formatVideoDuration(loomVideoDurations + mediaVideoDurations)}</Text>;
	}

	return <Text>{formatVideoDuration(loomVideoDurations)}</Text>;
};

export const ReadTimePopupWithErrorBoundary = (props: ReadTimePopupProps) => {
	return (
		<ErrorBoundary attribution={Attribution.SMARTS}>
			<ReadTimePopup {...props} />
		</ErrorBoundary>
	);
};

const CommonReadTimePopupSpinner = () => <Spinner size="small" />;
